import React, { useMemo, useEffect, useState } from 'react';
import {
  Box,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import useAPI from '../../api';
import useTranslate from '../../Hooks/useTranslate';
import { culture, formatPayloadDate } from '../../Helpers/dates';
import resolveValuePath from '../../Helpers/resolveValuePath';
import { resolveIcon } from '../../Utils/constants/icons';
import DateRangeInput from '../Inputs/DateRangeField';
import RangeNumbersInput from '../Inputs/RangeNumbersField';
import { ReadOnlyValue } from './ReadOnlyValue';
import { useFieldValue } from './useFieldValue';
import { useContextParameters } from './SchemaParameters';
import { useBindingScopes } from './binding/scopes';
import { contextNames, gatherParameters } from './binding/useFieldParameters';

/** The two ends of a period, as the text a payload can carry. */
const payloadRange = (range) =>
  (range ?? []).map((end) => formatPayloadDate(end)?.toISOString() ?? null);

/** A period, held as the `[from, to]` pair the range filters read. */
export const DateRangeField = ({ basePath, field }) => {
  const translate = useTranslate();
  const [value, setValue] = useFieldValue(basePath, field);

  const range = useMemo(
    () => [
      value?.[0] ? new Date(value[0]) : null,
      value?.[1] ? new Date(value[1]) : null,
    ],
    [value]
  );

  if (field.readOnly)
    return (
      <ReadOnlyValue
        id={field.key}
        label={field.label}
        value={
          range[0] && range[1]
            ? `${range[0].toLocaleDateString(culture)} - ${range[1].toLocaleDateString(culture)}`
            : ''
        }
      />
    );

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <DateRangeInput
        id={field.key}
        label={translate(field.label)}
        value={range}
        onChange={(newRange) =>
          setValue(
            newRange?.[0] || newRange?.[1] ? payloadRange(newRange) : null
          )
        }
      />
    </Box>
  );
};

/** Where an end of a scale takes its number from, when the entry does not say. */
const boundSource = (field, end) =>
  field[`${end}Source`] ?? (field[`${end}Parameter`] ? 'parameter' : 'number');

/**
 * What a field's declared lookups answer, as one object keyed by the root each was given.
 *
 * A source is a registry key like any other - `url` names it and `parameters` the runtime values
 * its procedure needs, as `contextParameters` does for a lookup field. Merging them under their
 * roots leaves a path the only way anything addresses an answer, so one source can serve several
 * readers and several sources can serve one.
 *
 * Only the sources some path names are called.
 */
const useLookupSources = (sources, key, paths, basePath = null) => {
  const API = useAPI();
  const wanted = useMemo(
    () =>
      (sources ?? []).filter(
        ({ root, url }) =>
          root && url && paths.some((path) => path?.split('.')[0] === root)
      ),
    [JSON.stringify(sources), JSON.stringify(paths)]
  );
  const names = useMemo(
    () => [...new Set(wanted.flatMap((source) => contextNames(source)))],
    [wanted]
  );
  const ambient = useContextParameters(names);
  const { form } = useBindingScopes({
    basePath,
    reads: wanted.some((source) => (source.dependsOn ?? []).length > 0),
  });
  const [answers, setAnswers] = useState(null);
  // What each source is asked with, so a source whose sibling changed is called again and one still
  // waiting for it is not called at all.
  const asked = useMemo(
    () =>
      wanted.map((source) => ({
        root: source.root,
        url: source.url,
        ...gatherParameters(source, ambient, form),
      })),
    [wanted, ambient, form]
  );
  const signature = JSON.stringify({ asked, key });

  useEffect(() => {
    if (asked.length === 0) return undefined;

    let isMounted = true;

    setAnswers(null);
    asked.forEach(({ root, url, parameters, ready }) => {
      if (!ready) {
        setAnswers((current) => ({ ...current, [root]: null }));

        return;
      }

      API.lookupDynamicOptionsBySearch(
        url,
        { Key: key, ...parameters },
        (res) => {
          if (isMounted)
            setAnswers((current) => ({ ...current, [root]: res?.[0] ?? null }));
        },
        () => {
          if (isMounted)
            setAnswers((current) => ({ ...current, [root]: null }));
        }
      );
    });

    return () => {
      isMounted = false;
    };
  }, [signature]);

  return answers;
};

/**
 * A span between two numbers, held as the `[min, max]` pair the range filters read.
 *
 * Each end of the scale comes from one of three places, as its `minSource` / `maxSource` says: the
 * number the layout declares, a runtime value it names (`minParameter`, `maxParameter`), or the
 * lookups the field declares in `sources`.
 *
 * The lookups answer into one object keyed by the root each was given, and `minPath` / `maxPath` say
 * where in it each end reads - `range.min`, or any dotted path. Two ends naming the same root share
 * one call; naming different ones makes two.
 *
 * The declared number is the fallback when the named source has nothing to give, so the slider
 * always has a scale.
 */
export const NumberRangeField = ({ basePath, field }) => {
  const translate = useTranslate();
  const names = useMemo(
    () =>
      [
        boundSource(field, 'min') === 'parameter' && field.minParameter,
        boundSource(field, 'max') === 'parameter' && field.maxParameter,
      ].filter(Boolean),
    [field]
  );
  const parameters = useContextParameters(names);
  const paths = useMemo(
    () =>
      [
        boundSource(field, 'min') === 'lookup' && field.minPath,
        boundSource(field, 'max') === 'lookup' && field.maxPath,
      ].filter(Boolean),
    [field]
  );
  const answers = useLookupSources(field.sources, field.key, paths, basePath);

  /** The number an end resolves to, or nothing when its source has none to give. */
  const bound = (end) => {
    const source = boundSource(field, end);

    if (source === 'lookup')
      return resolveValuePath(answers, field[`${end}Path`]);

    if (source === 'parameter') return parameters[field[`${end}Parameter`]];

    return undefined;
  };

  const min = Math.floor(bound('min') ?? field.min ?? 0);
  const max = Math.ceil(bound('max') ?? field.max ?? 100);

  const [value, setValue] = useFieldValue(basePath, field);

  // A drag is held here and written to the store once, on release: every write re-renders the
  // fields around this one, which a mouse move should not.
  const [dragged, setDragged] = useState(null);
  const span = dragged ?? [value?.[0] ?? min, value?.[1] ?? max];
  const unit = field.unit ? ` ${field.unit}` : '';

  if (field.readOnly)
    return (
      <ReadOnlyValue
        id={field.key}
        label={field.label}
        value={value ? `${span[0]}${unit} - ${span[1]}${unit}` : ''}
      />
    );

  return (
    <Box sx={{ p: '8px 12px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body2">{translate(field.label)}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {`${span[0]}${unit} – ${span[1]}${unit}`}
        </Typography>
      </Box>
      <Slider
        value={span}
        min={min}
        max={max}
        step={field.step ?? 1}
        disableSwap
        valueLabelDisplay="auto"
        getAriaLabel={() => field.key}
        onChange={(_, newSpan) => setDragged([...newSpan])}
        onChangeCommitted={(_, newSpan) => {
          setDragged(null);
          // The whole scale is no filter at all.
          setValue(
            newSpan[0] === min && newSpan[1] === max ? null : [...newSpan]
          );
        }}
      />
    </Box>
  );
};

/** The three answers a tri-state gives, in the order they are offered. */
const TRISTATE_CHOICES = [
  { value: 1, label: 'YES', icon: 'trueIcon' },
  { value: 0, label: 'NO', icon: 'falseIcon' },
  { value: null, label: 'ALL', icon: 'allIcon' },
];

/**
 * Yes, no, or neither - the shape a filter needs when "not set" is a third answer rather than the
 * absence of one, as for called / not called / all.
 *
 * Held as `1`, `0` or `null`, so the value is what the query carries. `trueIcon`, `falseIcon` and
 * `allIcon` name icons to show instead of the labels.
 */
export const TriStateField = ({ basePath, field }) => {
  const translate = useTranslate();
  const [value, setValue] = useFieldValue(basePath, field);

  const chosen = TRISTATE_CHOICES.find((choice) => choice.value === value);

  if (field.readOnly)
    return (
      <ReadOnlyValue
        id={field.key}
        label={field.label}
        value={translate(chosen?.label ?? 'ALL')}
      />
    );

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="body2" sx={{ opacity: '70%' }}>
        {translate(field.label)}
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        // A group cannot hold null as a value, so the choice is carried as its position.
        value={String(TRISTATE_CHOICES.indexOf(chosen ?? TRISTATE_CHOICES[2]))}
        onChange={(_, position) => {
          if (position !== null)
            setValue(TRISTATE_CHOICES[Number(position)].value);
        }}
        sx={{ mt: '4px' }}
      >
        {TRISTATE_CHOICES.map((choice, position) => {
          const name = field[choice.icon];
          const Icon = name ? resolveIcon(name) : null;

          return (
            <ToggleButton key={choice.label} value={String(position)}>
              {Icon ? (
                <Tooltip arrow title={translate(choice.label)}>
                  <Icon fontSize="small" />
                </Tooltip>
              ) : (
                translate(choice.label)
              )}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};

/**
 * A set of numbers written as an expression - `1,3-5` - matched as a list, or excluded from one.
 *
 * Held as `{ rawValue, value, operator }`: the expression as typed, the numbers it expands to, and
 * whether they are the wanted set or the unwanted one.
 */
export const NumberSetField = ({ basePath, field }) => {
  const [value, setValue] = useFieldValue(basePath, field);

  if (field.readOnly)
    return (
      <ReadOnlyValue
        id={field.key}
        label={field.label}
        value={value?.rawValue ?? ''}
      />
    );

  return (
    <RangeNumbersInput
      name={field.label}
      defaultValue={{
        rawValue: value?.rawValue,
        exclude: value?.operator === '!in',
      }}
      onChangeRangeValue={({ rawValue, range, exclude }) =>
        setValue(
          range?.length > 0
            ? { rawValue, value: range, operator: exclude ? '!in' : 'in' }
            : null
        )
      }
    />
  );
};
